// UAE gold pricing helpers. AED is pegged to USD at 3.6725 (since 1997).
// We derive every AED rate from the live international spot price.

export const AED_PER_USD = 3.6725;
export const GRAMS_PER_OZ = 31.1035;
export const GRAMS_PER_TOLA = 11.6638;

// Local Dubai jeweller premium (AED per gram, 24K). Small, transparent.
export const LOCAL_PREMIUM_AED_PER_G = 2.5;

export type Emirate = {
  slug: string;
  name: string;
  /** AED-per-gram premium over the UAE base (0 = base). */
  premium: number;
};

export const EMIRATES: Emirate[] = [
  { slug: "dubai", name: "Dubai", premium: 0 },
  { slug: "abu-dhabi", name: "Abu Dhabi", premium: 0.3 },
  { slug: "sharjah", name: "Sharjah", premium: 0.4 },
  { slug: "ajman", name: "Ajman", premium: 0.5 },
  { slug: "al-ain", name: "Al Ain", premium: 0.5 },
  { slug: "ras-al-khaimah", name: "Ras Al Khaimah", premium: 0.6 },
  { slug: "fujairah", name: "Fujairah", premium: 0.7 },
  { slug: "umm-al-quwain", name: "Umm Al Quwain", premium: 0.7 },
];

export type UaeKaratPrices = {
  perGram24: number;
  perGram22: number;
  perGram21: number;
  perGram18: number;
  perGram916: number;
  per8g24: number;
  per10g24: number;
  perTola24: number;
  perOunce24: number;
};

export function computeUaePrices(args: {
  spotUsdPerOz: number;
  emiratePremium?: number;
}): UaeKaratPrices {
  const safeSpot = Number.isFinite(args.spotUsdPerOz) ? args.spotUsdPerOz : 0;
  const aedPerGram24Base = (safeSpot * AED_PER_USD) / GRAMS_PER_OZ + LOCAL_PREMIUM_AED_PER_G;
  const perGram24 = aedPerGram24Base + (args.emiratePremium ?? 0);
  const perGram22 = perGram24 * (22 / 24);
  const perGram21 = perGram24 * (21 / 24);
  const perGram18 = perGram24 * (18 / 24);
  const perGram916 = perGram24 * 0.916;
  return {
    perGram24,
    perGram22,
    perGram21,
    perGram18,
    perGram916,
    per8g24: perGram24 * 8,
    per10g24: perGram24 * 10,
    perTola24: perGram24 * GRAMS_PER_TOLA,
    perOunce24: perGram24 * GRAMS_PER_OZ,
  };
}

export function formatAed(n: number): string {
  const safe = Number.isFinite(n) ? n : 0;
  return "AED " + Math.round(safe).toLocaleString("en-AE");
}

export function formatAedDecimal(n: number, dp = 2): string {
  const safe = Number.isFinite(n) ? n : 0;
  return "AED " + safe.toLocaleString("en-AE", { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

export const UAE_FAQS: { q: string; a: string }[] = [
  {
    q: "What is the gold rate in UAE today?",
    a: "The live gold rate in UAE updates every 10 minutes from the international spot price (XAU/USD) converted to AED at the official 3.6725 USD peg. Dubai 24K gold is shown per gram, per 8 gram (standard chain weight), per tola, and per ounce.",
  },
  {
    q: "Why is gold cheaper to buy in Dubai compared to India or Pakistan?",
    a: "Dubai charges only 5% VAT on jewellery (refundable for tourists) and has no import duty on investment-grade gold. Making charges at the Dubai Gold Souk are typically 30-50% lower than Indian metro jewellers. That is why expats and tourists trust Dubai for wedding and investment gold.",
  },
  {
    q: "What is the difference between 24K, 22K, 21K and 18K gold in UAE?",
    a: "24K is 99.9% pure gold — soft, used for bars and coins. 22K (916 hallmark) is 91.6% pure and is the most popular karat for jewellery in UAE. 21K (87.5%) is widely used in Gulf and Arab-style jewellery. 18K (75%) is preferred for diamond settings because it is harder.",
  },
  {
    q: "How is the per-gram gold rate calculated in UAE?",
    a: "(International spot USD/oz × 3.6725 AED/USD) ÷ 31.1035 g + local Dubai market premium. The result is the 24K AED/gram price. 22K is 91.67% of that, 21K is 87.5%, and 18K is 75%. We refresh this every 10 minutes from a live feed.",
  },
  {
    q: "Where can I buy gold in Dubai at the best rate?",
    a: "The Dubai Gold Souk in Deira is the world's largest gold market by trading volume. Trusted retailers include Malabar Gold & Diamonds, Joyalukkas, Damas, Kanz Jewels, and Pure Gold. Always check the daily Khaleej Times gold rate before bargaining and ask for the BIS/Dubai hallmark certificate.",
  },
  {
    q: "Is gold a good investment in UAE in 2026?",
    a: "Gold has historically protected wealth against AED inflation, USD volatility and geopolitical risk. With central-bank buying at record highs and the US Federal Reserve cutting rates, most Gulf wealth managers continue to recommend a 10-15% allocation to physical gold for long-term portfolios.",
  },
  {
    q: "What is the nisab for zakat on gold in UAE?",
    a: "Nisab is 87.48 grams of gold. If you own more than this amount in gold (or its AED equivalent in cash + savings + tradeable assets) for a full lunar year, you owe 2.5% as zakat. Our live zakat calculator below shows your nisab threshold in AED using today's rate.",
  },
  {
    q: "Can tourists get a VAT refund on gold purchased in UAE?",
    a: "Yes. Tourists spending more than AED 250 at participating retailers can claim back the 5% VAT at Dubai, Abu Dhabi or Sharjah international airports via Planet Tax Free kiosks. Carry the original tax invoice and the unworn jewellery in its sealed packaging.",
  },
  {
    q: "How often does the gold rate change in Dubai?",
    a: "Wholesale gold rates change every few seconds on the global market. UAE retailers like Malabar and Joyalukkas refresh their counter rates 2-3 times per day. Our page recomputes the AED rate every 10 minutes so you always have the latest indicative price.",
  },
  {
    q: "What is 916 gold and is it the same as 22K?",
    a: "Yes — 916 gold means 91.6% pure, which is exactly 22 karats out of 24. The '916' hallmark is the BIS/Dubai stamp guaranteeing 22K purity. It is the most-bought jewellery purity across UAE, India and the wider Gulf.",
  },
  {
    q: "Will the gold rate go up or down tomorrow in UAE?",
    a: "Tomorrow's gold rate depends on USD strength, US Treasury yields, geopolitical events and central-bank buying. We publish a rule-based daily outlook in the Forecast section based on the 7-day spot trend. No one can predict gold with certainty — use it as guidance, not advice.",
  },
  {
    q: "Is the Dubai gold rate the same as Abu Dhabi or Sharjah?",
    a: "The wholesale rate is identical across all seven emirates because they all source from the same DMCC-regulated bullion market. Retail prices differ by 0.3-0.7 AED per gram due to small variations in jeweller premiums, rent and labour costs. See the city-wise comparison below.",
  },
  {
    q: "What is the Dubai Gold Souk and is it safe?",
    a: "Dubai Gold Souk is a regulated traditional market in Deira with over 380 retailers. It is fully safe — monitored by Dubai Police and the Dubai Multi Commodities Centre (DMCC). All gold sold carries a government hallmark guaranteeing purity.",
  },
  {
    q: "How much gold is allowed to carry from Dubai to India?",
    a: "Male passengers can carry up to 20 grams (worth max INR 50,000) duty-free; female passengers up to 40 grams (max INR 100,000). Beyond that, customs duty of approximately 13.75% applies. Always declare honestly to avoid confiscation.",
  },
  {
    q: "Is the gold rate the same at Malabar, Joyalukkas and Damas?",
    a: "The base 22K and 24K wholesale rates are typically identical because all major chains publish using the Dubai Gold & Jewellery Group benchmark. Final price differs by making charges (usually 5-20% extra) and design complexity, not the metal rate itself.",
  },
];
